"use client";

import { c as _c } from "react/compiler-runtime";
import { NodeViewWrapper, NodeViewContent, type ReactNodeViewProps } from "@tiptap/react";
import { Trash2 } from "lucide-react";
import { WIDTH_PRESETS } from "./constants";
import "./figure.css";

/**
 * React NodeView for the Figure node. Renders the image + editable caption with
 * a small hover-revealed toolbar of width presets (25 / 50 / 75 / Full).
 *
 * Presets (rather than a selection-based drag handle) because the figure has an
 * editable caption: a click lands in the caption instead of creating a node
 * selection, so a handle gated on `selected` never shows. The toolbar lives
 * inside the node view and calls `updateAttributes` directly — no selection
 * needed — and each click is a single, undoable transaction.
 */
export function FigureNodeView(t0) {
  const $ = _c(25);
  const {
    node,
    updateAttributes,
    deleteNode
  } = t0;
  const width = node.attrs.width as number | null;
  const src = node.attrs.src as string | null;
  const alt = node.attrs.alt as string | null ?? "";
  const naturalWidth = node.attrs.naturalWidth as number | null;
  const naturalHeight = node.attrs.naturalHeight as number | null;
  const aspectRatio = naturalWidth && naturalHeight ? `${naturalWidth} / ${naturalHeight}` : undefined;
  const t1 = width ? `${width}%` : undefined;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      width: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = src ?? undefined;
  let t4;
  if ($[2] !== aspectRatio) {
    t4 = aspectRatio ? {
      aspectRatio
    } : undefined;
    $[2] = aspectRatio;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== alt || $[5] !== t3 || $[6] !== t4) {
    t5 = <img src={t3} alt={alt} draggable={false} className="chapter-figure__img" style={t4} />;
    $[4] = alt;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== updateAttributes || $[9] !== width) {
    t6 = WIDTH_PRESETS.map(pct => <button key={pct} type="button" className="chapter-figure__size-btn" data-active={width === pct ? "true" : undefined} aria-pressed={width === pct} onMouseDown={_temp} onClick={() => updateAttributes({
      width: pct
    })}>{pct === 100 ? "Full" : `${pct}%`}</button>);
    $[8] = updateAttributes;
    $[9] = width;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="chapter-figure__toolbar-divider" aria-hidden="true" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== deleteNode) {
    t8 = () => deleteNode();
    $[12] = deleteNode;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Trash2 size={14} aria-hidden="true" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8) {
    t10 = <button type="button" className="chapter-figure__size-btn chapter-figure__delete-btn" aria-label="Delete image" title="Delete image" onMouseDown={_temp2} onClick={t8}>{t9}</button>;
    $[15] = t8;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t6) {
    t11 = <div className="chapter-figure__toolbar" contentEditable={false} role="toolbar" aria-label="Image controls">{t6}{t7}{t10}</div>;
    $[17] = t10;
    $[18] = t6;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <NodeViewContent as="figcaption" className="chapter-figure__caption" />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t11 || $[22] !== t2 || $[23] !== t5) {
    t13 = <NodeViewWrapper as="figure" className="chapter-figure"><div className="chapter-figure__frame" style={t2}>{t5}{t11}{t12}</div></NodeViewWrapper>;
    $[21] = t11;
    $[22] = t2;
    $[23] = t5;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp(e) {
  return e.preventDefault();
}